import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bug, CheckCircle2, ImagePlus, Inbox, Lightbulb, Loader2, MessageSquare, Plus, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { API } from '../../utils/config';

const CATEGORIES = {
  suggerimento: { label: 'Suggerimento', icon: Lightbulb, className: 'bg-amber-100 text-amber-800' },
  bug: { label: 'Bug', icon: Bug, className: 'bg-red-100 text-red-800' },
  altro: { label: 'Altro', icon: MessageSquare, className: 'bg-slate-100 text-slate-700' },
};

const SecureImage = ({ id, index }) => {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let url;
    fetch(`${API}/api/segnalazioni/attachments/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('hw_token')}` },
    }).then(r => r.ok ? r.blob() : Promise.reject()).then(blob => {
      url = URL.createObjectURL(blob);
      setSrc(url);
    }).catch(() => setSrc(''));
    return () => url && URL.revokeObjectURL(url);
  }, [id]);
  return src ? <a href={src} target="_blank" rel="noreferrer" data-testid={`report-attachment-link-${index}`}><img src={src} alt={`Allegato ${index + 1}`} className="h-24 w-24 object-cover rounded border" /></a> : null;
};

export default function Segnalazioni() {
  const [box, setBox] = useState('unread');
  const [reports, setReports] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [reply, setReply] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [form, setForm] = useState({ category: 'suggerimento', property_id: '', subject: '', message: '' });
  const createFiles = useRef(null);
  const replyInput = useRef(null);
  const token = localStorage.getItem('hw_token');

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const [reportRes, propertyRes] = await Promise.all([
        fetch(`${API}/api/segnalazioni?box=${box}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/responsible/my-properties`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!reportRes.ok) throw new Error();
      setReports(await reportRes.json());
      if (propertyRes.ok) setProperties(await propertyRes.json());
    } catch {
      toast.error('Impossibile caricare le segnalazioni');
    } finally {
      setLoading(false);
    }
  }, [box, token]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const uploadFiles = async files => {
    const valid = Array.from(files || []).slice(0, 5);
    const ids = [];
    for (const file of valid) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
        throw new Error('Usa immagini JPG, PNG o WEBP fino a 5 MB');
      }
      const data = new FormData();
      data.append('file', file);
      const response = await fetch(`${API}/api/segnalazioni/attachments`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: data,
      });
      if (!response.ok) throw new Error((await response.json()).detail || 'Errore caricamento immagine');
      ids.push((await response.json()).id);
    }
    return ids;
  };

  const createReport = async event => {
    event.preventDefault();
    setSending(true);
    try {
      const attachment_ids = await uploadFiles(createFiles.current?.files);
      const response = await fetch(`${API}/api/segnalazioni`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, property_id: form.property_id || null, attachment_ids }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Invio non riuscito');
      toast.success('Segnalazione inviata all’amministrazione');
      setForm({ category: 'suggerimento', property_id: '', subject: '', message: '' });
      if (createFiles.current) createFiles.current.value = '';
      setShowForm(false);
      setBox('read');
      loadReports();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  const openReport = async report => {
    const response = await fetch(`${API}/api/segnalazioni/${report.id}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) return toast.error('Impossibile aprire la segnalazione');
    const data = await response.json();
    setSelected(data.report);
    setMessages(data.messages || []);
    window.dispatchEvent(new CustomEvent('ticketRead'));
    loadReports();
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const attachment_ids = await uploadFiles(replyFiles);
      const response = await fetch(`${API}/api/segnalazioni/${selected.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: reply, attachment_ids }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Invio non riuscito');
      setReply(''); setReplyFiles([]);
      if (replyInput.current) replyInput.current.value = '';
      await openReport(selected);
      toast.success('Risposta inviata');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 min-w-0 p-4 pt-16 md:p-6" data-testid="responsible-reports-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="text-3xl font-bold text-slate-900">Segnalazioni</h1><p className="text-slate-500">Suggerimenti e problemi tecnici con risposta diretta dell’amministrazione.</p></div>
        <Button onClick={() => setShowForm(v => !v)} data-testid="new-report-button"><Plus className="h-4 w-4 mr-2" />Nuova segnalazione</Button>
      </div>

      {showForm && <Card className="p-5 border-blue-200" data-testid="new-report-form-card">
        <form onSubmit={createReport} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label htmlFor="report-category">Categoria</Label><select id="report-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full h-10 border rounded px-3 mt-1" data-testid="report-category-select">{Object.entries(CATEGORIES).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}</select></div>
            <div><Label htmlFor="report-property">Immobile (facoltativo)</Label><select id="report-property" value={form.property_id} onChange={e => setForm({ ...form, property_id: e.target.value })} className="w-full h-10 border rounded px-3 mt-1" data-testid="report-property-select"><option value="">Piattaforma generale</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
          </div>
          <div><Label htmlFor="report-subject">Oggetto</Label><Input id="report-subject" required minLength={3} value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="report-subject-input" /></div>
          <div><Label htmlFor="report-message">Descrizione</Label><Textarea id="report-message" required minLength={3} rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="break-words" data-testid="report-message-input" /></div>
          <div><Label htmlFor="report-images">Immagini (massimo 5)</Label><Input id="report-images" ref={createFiles} type="file" multiple accept="image/jpeg,image/png,image/webp" data-testid="report-images-input" /></div>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setShowForm(false)} data-testid="cancel-report-button">Annulla</Button><Button type="submit" disabled={sending} data-testid="submit-report-button">{sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Invia</Button></div>
        </form>
      </Card>}

      <div className="flex gap-2 border-b" data-testid="report-box-tabs">
        <button onClick={() => setBox('unread')} className={`px-4 py-3 font-medium border-b-2 ${box === 'unread' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500'}`} data-testid="reports-unread-tab"><Inbox className="inline h-4 w-4 mr-2" />Da leggere</button>
        <button onClick={() => setBox('read')} className={`px-4 py-3 font-medium border-b-2 ${box === 'read' ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500'}`} data-testid="reports-read-tab"><CheckCircle2 className="inline h-4 w-4 mr-2" />Lette</button>
      </div>

      {loading ? <div className="py-16 text-center" data-testid="reports-loading"><Loader2 className="h-7 w-7 animate-spin mx-auto text-blue-600" /></div> : reports.length === 0 ? <div className="py-16 text-center text-slate-500" data-testid="reports-empty-state">Nessuna segnalazione in questa sezione.</div> : <div className="grid gap-3">{reports.map(report => {
        const category = CATEGORIES[report.report_category] || CATEGORIES.altro;
        const Icon = category.icon;
        return <button key={report.id} onClick={() => openReport(report)} className="w-full text-left border bg-white hover:border-blue-300 p-4 rounded transition-colors min-w-0" data-testid={`report-row-${report.id}`}><div className="flex items-start justify-between gap-3 min-w-0"><div className="min-w-0"><span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${category.className}`}><Icon className="h-3 w-3" />{category.label}</span><h2 className="font-semibold text-slate-900 mt-2 break-words [overflow-wrap:anywhere]">{report.subject}</h2><p className="text-sm text-slate-500 break-words">{report.property_name}</p></div><div className="shrink-0 text-right"><span className={`text-xs px-2 py-1 rounded ${report.status === 'chiuso' ? 'bg-slate-100' : 'bg-emerald-100 text-emerald-700'}`}>{report.status === 'chiuso' ? 'Chiusa' : 'Aperta'}</span><p className="text-xs text-slate-400 mt-2">{new Date(report.updated_at || report.created_at).toLocaleDateString('it-IT')}</p></div></div></button>;
      })}</div>}

      {selected && <div className="fixed inset-0 bg-black/50 z-50 p-4 flex items-center justify-center" data-testid="report-detail-modal"><div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col min-w-0"><div className="p-4 border-b flex justify-between gap-3"><div className="min-w-0"><h2 className="font-bold text-lg break-words [overflow-wrap:anywhere]" data-testid="report-detail-subject">{selected.subject}</h2><p className="text-sm text-slate-500">{selected.property_name}</p></div><Button variant="ghost" size="icon" onClick={() => setSelected(null)} data-testid="close-report-modal-button"><X className="h-5 w-5" /></Button></div><div className="p-4 overflow-y-auto space-y-4">{messages.map((message, messageIndex) => <div key={message.id} className={`flex ${message.sender_type === 'responsabile' ? 'justify-end' : 'justify-start'}`}><div className={`max-w-[88%] min-w-0 p-3 rounded ${message.sender_type === 'responsabile' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-900'}`} data-testid={`report-message-${messageIndex}`}><p className="text-xs font-semibold mb-1">{message.sender_type === 'responsabile' ? 'Tu' : 'Amministrazione'}</p><p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-sm">{message.content}</p>{message.attachments?.length > 0 && <div className="flex flex-wrap gap-2 mt-3">{message.attachments.map((id, index) => <SecureImage key={id} id={id} index={`${messageIndex}-${index}`} />)}</div>}</div></div>)}</div>{selected.status !== 'chiuso' ? <div className="p-4 border-t space-y-3"><Textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Scrivi una risposta…" rows={3} data-testid="report-reply-input" /><div className="flex flex-wrap justify-between gap-2"><label className="inline-flex items-center gap-2 text-sm cursor-pointer"><ImagePlus className="h-4 w-4" />Aggiungi immagini<input ref={replyInput} type="file" multiple accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={e => setReplyFiles(Array.from(e.target.files || []))} data-testid="report-reply-images-input" /></label><Button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="send-report-reply-button"><Send className="h-4 w-4 mr-2" />Invia risposta</Button></div></div> : <div className="p-4 border-t text-sm text-slate-500" data-testid="report-closed-notice">Segnalazione chiusa dall’amministrazione.</div>}</div></div>}
    </div>
  );
}